// Chart-convention map glyphs, drawn in-house in the SeaState monoline style.
// The SEMANTICS follow mariner convention (ECDIS/Chart No. 1): an anchor marks
// a harbor, an oriented triangle is an AIS target. The artwork is ours — the
// official S-52 symbol files are IHO-copyrighted (and the open sets are GPL),
// so we draw the conventions rather than copy the files. Each glyph is baked
// with a halo so it reads on the sea, rasterized at 2× for retina.
//
// Both the glyph stroke AND the halo are read from CSS custom properties, so the
// icons re-cast across the Day/Dusk/Night ECDIS tables — refreshMapIcons() re-bakes
// and swaps them on a theme change (the raster is otherwise frozen at register time).

import type { Map as MlMap } from "maplibre-gl";

/** Monoline anchor, stroke + halo colors baked in. */
function anchorSvg(color: string, halo: string, size: number): string {
  const glyph =
    `<circle cx="12" cy="4.6" r="2.2"/>` +
    `<line x1="12" y1="6.8" x2="12" y2="20.2"/>` +
    `<line x1="7.4" y1="10" x2="16.6" y2="10"/>` +
    `<path d="M4.4 14.2 C5.2 18.4 8.2 20.6 12 21 C15.8 20.6 18.8 18.4 19.6 14.2"/>` +
    `<path d="M4.4 14.2 L2.6 16.4 M4.4 14.2 L6.8 15.4"/>` +
    `<path d="M19.6 14.2 L21.4 16.4 M19.6 14.2 L17.2 15.4"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" ` +
    `stroke-linecap="round" stroke-linejoin="round">` +
    `<g stroke="${halo}" stroke-width="4.2">${glyph}</g>` +
    `<g stroke="${color}" stroke-width="1.9">${glyph}</g></svg>`
  );
}

/** Ringed anchor — the fleet's home ports (staging bases). */
function homePortSvg(color: string, halo: string, size: number): string {
  const glyph =
    `<circle cx="14" cy="14" r="12.2"/>` +
    `<circle cx="14" cy="7.8" r="1.9"/>` +
    `<line x1="14" y1="9.7" x2="14" y2="20.4"/>` +
    `<line x1="10.2" y1="12.4" x2="17.8" y2="12.4"/>` +
    `<path d="M7.8 15.8 C8.5 19.2 11 21 14 21.3 C17 21 19.5 19.2 20.2 15.8"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 28 28" fill="none" ` +
    `stroke-linecap="round" stroke-linejoin="round">` +
    `<g stroke="${halo}" stroke-width="4.6">${glyph}</g>` +
    `<g stroke="${color}" stroke-width="2">${glyph}</g></svg>`
  );
}

/** ECDIS-convention AIS target: a slim triangle, apex = course over ground. */
function aisTargetSvg(color: string, halo: string, size: number): string {
  const glyph = `<path d="M12 2.5 L18.5 21 L12 17.2 L5.5 21 Z"/>`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
    `stroke-linejoin="round">` +
    `<g fill="none" stroke="${halo}" stroke-width="4.4">${glyph}</g>` +
    `<g fill="${color}" stroke="${color}" stroke-width="1.4">${glyph}</g></svg>`
  );
}

function loadImage(svg: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("map icon failed to rasterize"));
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v !== "" ? v : fallback;
}

// The glyph set, baked with the palette LIVE right now. Names:
// port-navy | home-port | ais-target
//
// One anchor for every WPI harbour. The graded set (green/amber/red/grey, plus a
// tide-gated anchor-over-wave) was withdrawn with the draft-fit verdict it served:
// a glyph that grades has to be answering a question we can answer, and Pub 150
// depth codes are five-foot lower bounds, not soundings.
function iconDefs(): Array<[string, string]> {
  const halo = cssVar("--icon-halo", "#ffffff");
  return [
    ["port-navy", anchorSvg(cssVar("--navy", "#16232e"), halo, 40)],
    ["home-port", homePortSvg(cssVar("--accent", "#0f6b74"), halo, 52)],
    ["ais-target", aisTargetSvg(cssVar("--m1", "#2f6f9f"), halo, 48)],
  ];
}

let registered: Promise<void> | null = null;

/** Register the glyph set on the map (idempotent). */
export function registerMapIcons(map: MlMap): Promise<void> {
  if (registered !== null) return registered;
  registered = (async () => {
    const defs = iconDefs();
    const images = await Promise.all(defs.map(([, svg]) => loadImage(svg)));
    defs.forEach(([name], i) => {
      if (!map.hasImage(name)) map.addImage(name, images[i], { pixelRatio: 2 });
    });
  })().catch((err) => {
    registered = null; // allow a retry on the next ensure()
    throw err;
  });
  return registered;
}

/**
 * Re-bake every glyph with the CURRENT CSS palette and swap it in place. The raster
 * is frozen with the colours live at registration, so a Day→Dusk→Night switch leaves
 * the port anchors / home ports / AIS targets stale (and their white halos glaring on
 * the night ground). Call on a theme change; MapLibre re-renders the layers using them.
 */
export async function refreshMapIcons(map: MlMap): Promise<void> {
  const defs = iconDefs();
  const images = await Promise.all(defs.map(([, svg]) => loadImage(svg)));
  defs.forEach(([name], i) => {
    if (map.hasImage(name)) map.updateImage(name, images[i]);
    else map.addImage(name, images[i], { pixelRatio: 2 });
  });
}
